import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Grid,
  Typography,
  CardMedia,
  Avatar,
  Stack,
  IconButton,
  Divider,
  Button,
  TextField,
} from "@mui/material";
import { Favorite, FavoriteBorder, Comment, Close } from "@mui/icons-material";
import { deleteFeedComment, deleteFeedCommentReplay, getFeedComment, getFeeds, getSingleFeed, likeFeed, postFeedComment, postFeedCommentReplay, updateFeedComment, updateFeedCommentReplay } from "../../../../Redux/Reducers/communityReducer";
import { useUserDetailContext } from "../../../UserDetailContext";
import TelegramIcon from "@mui/icons-material/Telegram";
// import useFeedActions from "../../../../Community/FeedsHandle/useFeedActions";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ShareProfile } from "../../MyProfileDetails/ProfileConnectionComponents";
import FeedSkeleton from "../../../LoadingSkeleton/FeedSkeleton";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FeedSingleSkeleton from "../../../LoadingSkeleton/FeedSingleSkeleton";
import useFeedActions from "../FeedsHandle/useFeedActions";

const SingleFeedScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [editingReply, setEditingReply] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [commentText, setCommentText] = useState("");
  const [currentImgIdx, setCurrentImgIdx] = useState(0);
  const { userDetails } = useUserDetailContext();
  const userRefId = userDetails?.reference_id;
  const contentRef = React.useRef();
  const [isButtonVisible, setIsButtonVisible] = useState(false);

  // Get feed data from Redux store
  const feedsData = useSelector((state) => state.community.feeds.data);
  const singleFeed = useSelector((state) => state.community.singleFeed) || {};
  const { author, media, content, comments_count } = singleFeed || {};


  // Get comments
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [feeds, setFeeds] = useState(feedsData || []);
  const commentsData = useSelector((state) => state.community.comments?.comments);
  const [comments, setComments] = useState([]);
  const [localLiked, setLocalLiked] = useState(singleFeed?.has_liked);
  const [localLikesCount, setLocalLikesCount] = useState(singleFeed?.likes_count);
  const [localCommentsCount, setLocalCommentsCount] = useState(comments_count || 0);
  const [loadingFeed, setLoadingFeed] = useState(true);
  const [loadingComments, setLoadingComments] = useState(true);


  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingFeed(true);

        // If feed not in store, fetch all feeds
        if (!singleFeed) {
          await dispatch(getFeeds()); // Make sure to import getFeeds
        }

        // Fetch comments for this feed
        if (id) {
          setLoadingComments(true);
          await dispatch(getFeedComment(id));
          await dispatch(getSingleFeed(id));
          setLoadingComments(false);
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoadingFeed(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    if (commentsData) {
      setComments(commentsData);
      // Update local comments count when comments data changes
      setLocalCommentsCount(commentsData.length);
    }
  }, [commentsData]);

  useEffect(() => {
    if (singleFeed?.comments_count !== undefined) {
      setLocalCommentsCount(singleFeed.comments_count);
    }
  }, [singleFeed?.comments_count]);

  useEffect(() => {
    setLocalLiked(singleFeed?.has_liked);
    setLocalLikesCount(singleFeed?.likes_count);
  }, [singleFeed?.has_liked, singleFeed?.likes_count]);

  useEffect(() => {
    const el = contentRef.current;
    if (el) {
      const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight);
      const maxH = lineHeight * 2;
      setIsButtonVisible(el.scrollHeight > maxH);
    }
  }, [singleFeed?.content, expanded]);

  const {
    handleLike,
    handleLikeComment,
    handleFeedCommentReplyLike,
    handleSendComment,
    handleSendReply,
  } = useFeedActions(singleFeed?.id, comments, setComments, feeds, setFeeds);

  if (loadingFeed) {
    return (
      <Box sx={{ p: 1, textAlign: "center" }}>
        <FeedSingleSkeleton />
      </Box>
    );
  }


  const isPostAuthor = userRefId === author?.user_ref_id;

  const handleLikeOptimistic = () => {
    setLocalLiked((prev) => !prev);
    setLocalLikesCount((prev) => (localLiked ? prev - 1 : prev + 1));
    dispatch(likeFeed(singleFeed.id));
  };

  const handleThumbnailClick = (index) => {
    setCurrentImgIdx(index);


  };
  const handleAddComment = async () => {
    if (commentText.trim()) {
      // Optimistic update
      const newComment = {
        id: Date.now(), // Temporary ID
        content: commentText,
        author: {
          name: userDetails.name,
          profile_pic: userDetails.profile_pic,
          designation: userDetails.designation,
          user_ref_id: userDetails.reference_id
        },
        created_at: new Date().toISOString(),
        likes_count: 0,
        has_liked: false,
        replies: [],
        replies_count: 0
      };

      // Update local state optimistically
      setComments(prev => [newComment, ...prev]);
      setLocalCommentsCount(prev => prev + 1);

      try {
        const success = await handleSendComment(commentText);

        if (success) {
          setCommentText("");
          // Refresh comments to get server state
          await dispatch(getFeedComment(singleFeed.id));
        } else {
          // Rollback if failed
          setComments(prev => prev.filter(c => c.id !== newComment.id));
          setLocalCommentsCount(prev => prev - 1);
        }
      } catch (error) {
        // Rollback on error
        setComments(prev => prev.filter(c => c.id !== newComment.id));
        setLocalCommentsCount(prev => prev - 1);
        console.error('Error adding comment:', error);
      }
    }
  };

  const handleReplyToggle = (commentId) => {
    setReplyingTo((prev) => (prev === commentId ? null : commentId));
  };

  const handleDeleteComment = async (postId, commentId) => {
    // Optimistic update
    const commentToDelete = comments.find(c => c.id === commentId);
    if (!commentToDelete) return;

    // Store previous state for rollback
    const previousComments = [...comments];
    const previousCount = localCommentsCount;

    // Update UI immediately
    setComments(prev => prev.filter(c => c.id !== commentId));
    setLocalCommentsCount(prev => prev - 1);

    try {
      const res = await dispatch(
        deleteFeedComment({ id: postId, commentId: commentId })
      );
      if (res?.payload?.statusCode === 200) {
        // Refresh comments to ensure consistency
        await dispatch(getFeedComment(postId));
      } else {
        // Rollback on failure
        setComments(previousComments);
        setLocalCommentsCount(previousCount);
      }
    } catch (error) {
      // Rollback on error
      setComments(previousComments);
      setLocalCommentsCount(previousCount);
      console.error("Error deleting comment:", error);
    }
  };

  const handleShareEvent = () => {
    setShareModalOpen(true);
  };



  // ... (Include all your helper functions like formatTimeAgo, handleEditComment, etc.)

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const createdDate = new Date(dateString);
    const seconds = Math.floor((now - createdDate) / 1000);

    // Define time intervals in seconds
    const intervals = {
      year: 31536000, // 365 days
      month: 2592000, // 30 days
      week: 604800, // 7 days
      day: 86400, // 24 hours
      hour: 3600, // 60 minutes
      minute: 60,
    };

    // Calculate time differences
    if (seconds < 10) return "just now";
    if (seconds < 60) return `${seconds}s`;

    if (seconds < intervals.hour) {
      const minutes = Math.floor(seconds / intervals.minute);
      return `${minutes}m`;
    }

    if (seconds < intervals.day) {
      const hours = Math.floor(seconds / intervals.hour);
      return `${hours}h`;
    }

    if (seconds < intervals.week) {
      const days = Math.floor(seconds / intervals.day);
      return `${days}d`;
    }

    if (seconds < intervals.month) {
      const weeks = Math.floor(seconds / intervals.week);
      return `${weeks}w`;
    }

    if (seconds < intervals.year) {
      const months = Math.floor(seconds / intervals.month);
      return `${months}mo`;
    }

    const years = Math.floor(seconds / intervals.year);
    return `${years}y`;
  };

  const handleEditComment = async (postId, commentId) => {
    if (editingComment?.text.trim()) {
      const params = {
        id: postId,
        commentId: commentId,
        content: editingComment.text,
      };

      try {
        const res = await dispatch(updateFeedComment(params));

        // Check for fulfilled action and response structure
        if (res?.payload?.statusCode === 200) {
          dispatch(getFeedComment(postId));
        }
      } catch (error) {
        console.error("Failed to update comment:", error);
      } finally {
        setEditingComment(null);
      }
    }
  };

  const handleEditReply = async (postId, commentId, replyId) => {
    if (editingReply?.text.trim()) {
      const params = {
        id: postId,
        commentId: commentId,
        replyId: replyId,
        content: editingReply.text,
      };

      try {
        const res = await dispatch(updateFeedCommentReplay(params));

        if (res?.payload?.statusCode === 200) {
          dispatch(getFeedComment(postId));
        }
      } catch (error) {
        console.error("Failed to update reply:", error);
      } finally {
        setEditingReply(null);
      }
    }
  };

  const handleDeleteCommentReplay = async (postId, commentId, replyId) => {
    // console.log("postId, commentId", postId, commentId);
    const params = { id: postId, commentId: commentId, replyId: replyId };
    const res = await dispatch(deleteFeedCommentReplay(params));
    if (res?.payload?.statusCode === 200) {
      dispatch(getFeedComment(postId));
    }
  };

  const handleAddReply = async (postId, commentId) => {
    if (replyText.trim()) {
      try {
        const params = {
          id: postId,
          commentId: commentId,
          content: replyText,
        };
        await dispatch(postFeedCommentReplay(params));
        await dispatch(getFeedComment(postId));
        setReplyText("");
        setReplyingTo(null);
      } catch (error) {
        console.error("Error adding reply:", error);
      }
    }
  };

  const handleShareSuccess = (msg) => {
    // Optionally show a snackbar or toast
  };
  const handleShareFailure = (msg) => {
    // Optionally show a snackbar or toast
  };

  const formatOPostTimeAgo = (dateString) => {
    const now = new Date();
    const createdDate = new Date(dateString);
    const seconds = Math.floor((now - createdDate) / 1000);

    // Define time intervals in seconds
    const intervals = {
      year: 31536000, // 365 days
      month: 2592000, // 30 days
      week: 604800, // 7 days
      day: 86400, // 24 hours
      hour: 3600, // 60 minutes
      minute: 60,
    };

    // Calculate time differences
    if (seconds < 10) return "just now";
    if (seconds < 60) return `${seconds}s`;

    if (seconds < intervals.hour) {
      const minutes = Math.floor(seconds / intervals.minute);
      return `${minutes} minutes ago`;
    }

    if (seconds < intervals.day) {
      const hours = Math.floor(seconds / intervals.hour);
      return `${hours} hours ago`;
    }

    if (seconds < intervals.week) {
      const days = Math.floor(seconds / intervals.day);
      return `${days} days ago`;
    }

    if (seconds < intervals.month) {
      const weeks = Math.floor(seconds / intervals.week);
      return `${weeks} weeks ago`;
    }

    if (seconds < intervals.year) {
      const months = Math.floor(seconds / intervals.month);
      return `${months} months ago`;
    }

    const years = Math.floor(seconds / intervals.year);
    return `${years} years ago`;
  };



  const renderMedia = (mediaItem, idx) => {

    if (mediaItem?.type?.includes("video")) {
      return (
        <video
          src={mediaItem?.url}
          controls
          style={{
            width: "100%",
            height: 400,
            objectFit: "conatin",
            borderRadius: 8,
          }}
        />
      );
    } else {
      return (
        <CardMedia
          component="img"
          image={media[currentImgIdx]?.url}
          alt="media"
          sx={{
            width: "100%",
            height: "50vh",
            objectFit: "contain",
            borderRadius: 2,
            mb: 2,
            border: "1px solid lightgray",
          }}
        />
      );
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: "0 auto" }}>
      <Grid container spacing={3} >
        {/* Media Column */}
        <Grid item xs={12} md={7}>
          <Box sx={{ maxHeight: "80vh", overflowY: "scroll", scrollbarWidth: "none" }}>
            {/* <Button 
        variant="outlined" 
        // startIcon={<Close />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back
      </Button> */}
            <Button
              onClick={() => navigate("/community?tab=second", { state: { scrollToFeedId: id } })}
              sx={{
                // position: "absolute",
                mt: 2,
                bottom: 16,
                // left: 16,
                color: '#0044C6'
              }}
            >
              <ArrowBackIcon /> Back
            </Button>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Avatar src={author?.profile_pic} />
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {author?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {author?.designation}
                </Typography>
                <Typography variant="body2" align="left" sx={{ width: "100%", fontSize: "0.6rem" }}>
                  {formatOPostTimeAgo(singleFeed?.created_at)}
                </Typography>
              </Box>
            </Stack>

            {/* Content */}
            <Box mb={2}>
              <Typography
                ref={contentRef}
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: expanded ? "none" : 8,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "pre-line", // 👈 This preserves newlines
                }}
              >
                {content || ""}
              </Typography>

              {isButtonVisible && (
                <Button onClick={() => setExpanded(!expanded)} size="small">
                  {expanded ? "Show less" : "Show more"}
                </Button>
              )}
            </Box>
            {media?.length > 0 && (
              <Box>
                {renderMedia(media[currentImgIdx], currentImgIdx)}

                {/* Thumbnails */}
                {media?.length > 1 && (
                  <Grid container spacing={1} justifyContent="flex-start">
                    {media?.map((mediaItem, index) => {
                      const isVideo = mediaItem.type?.includes("video");
                      const thumbnailSrc = isVideo
                        ? mediaItem.thumbnail || mediaItem.url
                        : mediaItem.url;

                      return (
                        <Grid item key={mediaItem.id}>
                          {isVideo ? (
                            <Box
                              onClick={() => handleThumbnailClick(index)}
                              sx={{
                                position: "relative",
                                width: "8vh",
                                height: "8vh",
                                borderRadius: 1,
                                overflow: "hidden",
                                border: index === currentImgIdx ? "2px solid #1976d2" : "1px solid #e0e0e0",
                                cursor: "pointer",
                                opacity: index === currentImgIdx ? 1 : 0.7,
                              }}
                            >
                              <video
                                src={thumbnailSrc}
                                muted
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <PlayArrowIcon
                                sx={{
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  color: "black",
                                  fontSize: 24,
                                  backgroundColor: "white",
                                  borderRadius: "50%",
                                  padding: "4px",
                                }}
                              />
                            </Box>
                          ) : (
                            <CardMedia
                              component="img"
                              image={thumbnailSrc}
                              alt={`thumbnail-${index}`}
                              sx={{
                                width: "8vh",
                                height: "8vh",
                                objectFit: "cover",
                                borderRadius: 1,
                                cursor: "pointer",
                                border: index === currentImgIdx ? "2px solid #1976d2" : "1px solid #e0e0e0",
                                opacity: index === currentImgIdx ? 1 : 0.7,
                              }}
                              onClick={() => handleThumbnailClick(index)}
                            />
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Box>
            )}
            <Divider />

            <Stack direction="row" justifyContent="flex-start" sx={{ mb: 2 }}>
              <IconButton onClick={handleLikeOptimistic}>
                {localLiked ? <Favorite color="error" /> : <FavoriteBorder />}
                <Typography sx={{ ml: 0.5 }}>{localLikesCount}</Typography>
              </IconButton>
              <Button startIcon={<Comment />} sx={{ color: "text.secondary" }}>
                <Typography sx={{ ml: 0.5 }}>{localCommentsCount}</Typography>
              </Button>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShareEvent();
                }}
              >
                <TelegramIcon />
              </IconButton>
            </Stack>
          </Box>


        </Grid>


        {/* Content Column */}
        <Grid item xs={12} md={5}>
          {/* Comment Input */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <TextField
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault(); // Prevent new line if using multiline
                  handleAddComment(singleFeed.id);
                }
              }}
              size="small"
              fullWidth
              placeholder="Add a comment..."
            />
            <Button
              onClick={() => handleAddComment(singleFeed.id)}
              sx={{ ml: 1, textTransform: "none", color: "#0044C6" }}
            >
              Send
            </Button>
          </Box>
          <Box sx={{ overflowY: "auto", maxHeight: "75vh", p: 1 }}>



            {/* Comments Section */}
            <Typography variant="subtitle2" fontWeight="bold" mb={2}>
              {comments?.length > 0 ? "Comments" : "No comments yet"}
            </Typography>

            {/* Render comments here (same as in your modal) */}

            {comments?.map((comment) => (
              <Box key={comment?.id} sx={{ mb: 2 }}>
                {/* Comment Header */}
                <Box
                  sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}
                >
                  <Avatar
                    src={comment?.author?.profile_pic}
                    sx={{
                      mr: 1.5,
                      width: 32,
                      height: 32,
                      border: "1px solid #e0e0e0",
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "left",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      >
                        {comment?.author?.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: "light", ml: "10px" }}
                      >
                        {formatTimeAgo(comment?.created_at)}
                      </Typography>
                    </Box>

                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.7rem",
                      }}
                    >
                      {comment?.author?.designation}
                    </Typography>
                    {/* Comment Content */}
                    {editingComment?.id === comment?.id ? (
                      <Box sx={{ display: "flex", mt: 1 }}>
                        <TextField
                          value={editingComment?.text}
                          onChange={(e) =>
                            setEditingComment((prev) => ({
                              ...prev,
                              text: e.target.value,
                            }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault(); // Prevent new line if using multiline
                              handleEditComment(id, comment?.id)
                            }
                          }}
                          size="small"
                          fullWidth
                          autoFocus
                        />
                        <Button
                          onClick={() => handleEditComment(id, comment?.id)}
                          sx={{ ml: 1, color: "#0044C6" }}
                        >
                          Save
                        </Button>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {comment?.content}
                      </Typography>
                    )}
                  </Box>

                  {/* Edit/Delete Actions */}
                </Box>

                {/* Like/Reply Actions */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    ml: 5,
                    mt: 0.5,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleLikeComment(id, comment?.id)}
                  >
                    {comment?.has_liked ? (
                      <Favorite color="error" fontSize="small" />
                    ) : (
                      <FavoriteBorder fontSize="small" />
                    )}
                  </IconButton>
                  <Typography variant="caption" sx={{ mr: 2 }}>
                    {comment?.likes_count || 0}
                  </Typography>

                  <Button
                    size="small"
                    onClick={() => handleReplyToggle(comment?.id)}
                    sx={{ textTransform: "none", fontSize: "0.8rem", color: "#0044C6" }}
                  >
                    Reply
                  </Button>

                  {!editingComment && (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {comment?.author?.user_ref_id === userRefId && (
                        <Button
                          size="small"
                          onClick={() =>
                            setEditingComment({
                              id: comment?.id,
                              text: comment?.content,
                            })
                          }
                          sx={{
                            textTransform: "none",
                            fontSize: "0.8rem",
                            minWidth: 0,
                            padding: 0,
                            color: "#0044C6"
                          }}
                        >
                          Edit
                        </Button>
                      )}
                      {(comment?.author?.user_ref_id === userRefId ||
                        isPostAuthor) && (
                          <Button
                            size="small"
                            onClick={() =>
                              handleDeleteComment(singleFeed?.id, comment?.id)
                            }
                            sx={{
                              textTransform: "none",
                              fontSize: "0.8rem",
                              minWidth: 0,
                              padding: 0,
                              color: "error.main",
                            }}
                          >
                            Delete
                          </Button>
                        )}
                    </Box>
                  )}
                </Box>

                {/* Replies Section */}
                {comment?.replies?.length > 0 && (
                  <Box
                    sx={{
                      ml: 4.5,
                      borderLeft: "2px solid #e0e0e0",
                      pl: 2,
                      mt: 1,
                    }}
                  >
                    {comment?.replies?.map((reply) => (
                      <Box key={reply?.id} sx={{ mb: 1.5, mt: 1.5 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "flex-start" }}
                        >
                          <Avatar
                            src={reply?.author?.profile_pic}
                            sx={{
                              mr: 1.5,
                              width: 28,
                              height: 28,
                              border: "1px solid #e0e0e0",
                            }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Box
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                              >
                                {reply?.author?.name}
                              </Typography>

                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  ml: 1.5,
                                  fontSize: "0.65rem",
                                }}
                              >
                                {formatTimeAgo(reply?.created_at)}
                              </Typography>
                            </Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.7rem",
                              }}
                            >
                              {reply?.author?.designation}
                            </Typography>

                            {editingReply?.id === reply?.id ? (
                              <Box sx={{ display: "flex", mt: 1 }}>
                                <TextField
                                  value={editingReply?.text}
                                  onChange={(e) =>
                                    setEditingReply((prev) => ({
                                      ...prev,
                                      text: e.target.value,
                                    }))
                                  }
                                  size="small"
                                  fullWidth
                                  autoFocus
                                />
                                <Button
                                  onClick={() =>
                                    handleEditReply(
                                      id,
                                      comment?.id,
                                      reply?.id
                                    )
                                  }
                                  sx={{ ml: 1, color: "#0044C6" }}
                                >
                                  Save
                                </Button>
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {reply?.content}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            ml: 5,
                            mt: 0.5,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleFeedCommentReplyLike(
                                comment?.id,
                                reply?.id
                              )
                            }
                          >
                            {reply?.has_liked ? (
                              <Favorite color="error" fontSize="small" />
                            ) : (
                              <FavoriteBorder fontSize="small" />
                            )}
                          </IconButton>
                          <Typography variant="caption" sx={{ mr: 2 }}>
                            {reply?.likes_count || 0}
                          </Typography>
                          {reply?.author?.user_ref_id === userRefId &&
                            !editingReply && (
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                  size="small"
                                  onClick={() =>
                                    setEditingReply({
                                      id: reply?.id,
                                      text: reply?.content,
                                    })
                                  }
                                  sx={{
                                    textTransform: "none",
                                    fontSize: "0.8rem",
                                    minWidth: 0,
                                    padding: 0,
                                    color: "#0044C6"
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() =>
                                    handleDeleteCommentReplay(
                                      id,
                                      comment?.id,
                                      reply?.id
                                    )
                                  }
                                  sx={{
                                    textTransform: "none",
                                    fontSize: "0.8rem",
                                    minWidth: 0,
                                    padding: 0,
                                    color: "error.main",
                                  }}
                                >
                                  Delete
                                </Button>
                              </Box>
                            )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Reply Input */}
                {replyingTo === comment?.id && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mt: 1.5,
                      ml: 5,
                    }}
                  >
                    <Avatar
                      src={comment?.author?.profile_pic}
                      sx={{
                        mr: 1.5,
                        width: 32,
                        height: 32,
                        border: "1px solid #e0e0e0",
                      }}
                    />
                    <TextField
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault(); // Prevent new line if using multiline
                          handleAddReply(id, comment?.id)
                        }
                      }}
                      size="small"
                      fullWidth
                      placeholder="Write a reply..."
                    />
                    <Button
                      onClick={() => handleAddReply(id, comment?.id)}
                      sx={{ ml: 1, textTransform: "none" }}
                    >
                      Post
                    </Button>
                  </Box>
                )}
              </Box>
            ))}

            <ShareProfile
              qrCodeUrl={`${window.location.origin}/explore/feeds/${singleFeed.id}`}
              open={shareModalOpen}
              onClose={() => setShareModalOpen(false)}
              profileType="feeds"
              showQr={true}
              isDownload={false}
              handleSuccess={handleShareSuccess}
              handleFailure={handleShareFailure}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SingleFeedScreen;